import { useEffect, useState, useCallback } from "react";
import { orderTable as OrderTableType } from "../../../shared/services/interface";
import { getOrder, putOrderEdit } from "../../../shared/services/services";
import { errorHandling } from "src/shared/services/errorHandling";
import { closeLoading, notifyLoading, notifySuccess, notifyWarning } from "src/assets/toastService";
import { Order } from "src/shared/types";
import { orderReturnId } from "src/shared/api";

const useOrders = (
  refresh?: string,
  id?: string,
  page?: number,
  limit?: number,
  from?: string,
  to?: string,
  searchName?: string,
  staff?: string,
  field?: string,
  orderby?: string,
  statusFilter?: string,
) => {
  const [orderData, setOrderData] = useState<Order[]>([]);
  const [loadingOrder, setLoading] = useState<boolean>(false);
  const [orderTableData, setOrderTable] = useState<OrderTableType>();

  // ✅ Common Fetch Function
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      let res;

      // 👉 List API
      if (from && to) {
        res = await getOrder("", page, limit, from, to, searchName, staff || "", field, orderby, statusFilter);
      }
      // 👉 Single Order
      else if (id) {
        res = await getOrder(id);
      } else {
        setOrderData([]);
        return;
      }

      if (res?.data) {
        setOrderData(res.data.result || []);
        setOrderTable(res.data);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [id, page, limit, from, to, searchName, staff, field, orderby, statusFilter]);

  // ✅ useEffect with debounce
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchOrders();
    }, 400);

    return () => clearTimeout(delay);
  }, [refresh, fetchOrders]);

  // ✅ PUT + Auto Refresh
  const putOrderData = async (id: string, putData: any) => {
    try {
      await putOrderEdit(id, putData);
      await fetchOrders();
    } catch (error) {
      console.error("Update failed:", error);
      throw error;
    }
  };
  const searchOrderId = async (searchId: string, orderIdBased: (orders: Order[]) => void) => {
    await getOrder(searchId)
      .then((res) => {
        if (res?.data) {
          orderIdBased(res.data.result ? [res.data.result] : []);
        }
      })
      .catch((err) => {
        errorHandling(err);
      });
  };

  const orderReturn = async (id: string, data: any) => {
    notifyLoading("Processing return...");
    try {
      await orderReturnId(id, data).then((res) => {
        closeLoading();
        notifySuccess("Return processed successfully");
      });
    } catch (error) {
      console.error("Return failed:", error);
      closeLoading();
      notifyWarning("Return failed. Please try again.");
      throw error;
    }
  };

  return {
    orderData,
    loadingOrder,
    setOrderData,
    orderTable: orderTableData,
    putOrderData,
    refreshOrders: fetchOrders,
    searchOrderId,
    orderReturn,
  };
};

export default useOrders;
